// @ts-strict

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { of, Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, mapTo, tap } from 'rxjs/operators';
import Cookies from 'js-cookie';
import * as moment from 'moment';
import { environment } from '@env/environment';
import { BaseApiResponse, GoogleVerifyResponse, IUserBasicInfo, RegistrationSteps, Tokens, UserLogin } from '../models/core-models';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { IUserEditData, SourceInformationsResponse, UserInfoResponse, UserNipRegexResponse, UserZipCodeRegexResponse } from '../types/auth';
import { HttpHeaders } from '@angular/common/http';
import { SocketService } from '@services/socket.service';
import { GetUserBasicDataService } from '@services/get-user-basic-data/get-user-basic-data.service';
import { Router } from '@angular/router';
import { AppleSignInService } from '@services/apple/apple-sign-in.service';
import { CollectActivityService } from '@services/collect-activity/collect-activity.service';
import { DatePickerService } from '@services/date-picker/date-picker.service';
import { toNumber } from 'lodash';
import { GlobalNotificationsService } from '@services/global-notifications/global-notifications.service';
declare var gtag: Function;
declare global {
  interface Window {
    dataLayer: any[];
  }
}

const REFRESH_TOKEN = 'refresh_token';
const ACCESS_TOKEN = 'access_token';
const ADMIN_ACCESS_TOKEN = 'admin_access_token';
const ADMIN_REFRESH_TOKEN = 'admin_refresh_token';

export interface ITermsAndConditions {
  allTerms: boolean;
  terms: boolean;
  data_processing: boolean;
  marketing: boolean;
}

export interface INewUser {
  username: string;
  email: string;
  password: string;
  password_confirmation: string;
  terms: boolean;
  data_processing: boolean;
  marketing: boolean;
  lang: string | null;
  ref: number | null;
  exref: string;
}

export interface INewSocialsUser {
  username?: string;
  email?: string;
  terms: boolean;
  data_processing: boolean;
  marketing: boolean;
  lang: string | null;
  ref?: number | null;
  exref?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public registrationProgress$ = new BehaviorSubject<number>(1);
  public validInputsArray: string[] = [];
  public socialUser: string | null = null;
  public redirectionToUser = false;
  public registrationWithSocialsData:
    | {
        cache_id: string;
        required_fields: string[] | string;
      }
    | undefined;

  private httpOptions: object = {};
  private username: string = '';
  private password: string = '';
  private isLoginInProgress: boolean = false;
  public changePasswordRequired: boolean = false;
  // Dodajemy stałą dla przechowywania timera odświeżania tokenu
  private tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  updateProgress(value: number): void {
    const lastValue = this.registrationProgress$.getValue();
    this.registrationProgress$.next(lastValue + value);
  }

  clearValidInputsArray(): void {
    this.validInputsArray = [];
    this.registrationProgress$.next(1);
  }

  addValidInput(name: string) {
    if (!this.validInputsArray.includes(name)) {
      this.validInputsArray.push(name);
      this.updateProgress(6);
    }
  }

  removeInvalidInput(name: string) {
    if (this.validInputsArray.includes(name)) {
      const index = this.validInputsArray.findIndex(el => el === name);
      this.validInputsArray.splice(index, 1);
      this.updateProgress(-6);
    }
  }

  // available steps: "step1"- send email with link, "step2"- waiting for link confirmation, "step3-  set new password"
  //  when user clicks on the email link then resetPasswordActiveStep must change to "step3"
  resetPasswordActiveStep = new BehaviorSubject<RegistrationSteps>('step1');

  constructor(
    private http: HttpClient,
    public activeModal: NgbActiveModal,
    private router: Router,
    private socketService: SocketService,
    private getUserBasicDataService: GetUserBasicDataService,
    private appleAuthService: AppleSignInService,
    private activityData: CollectActivityService,
    private datePicker: DatePickerService,
    private globalNotifications: GlobalNotificationsService
  ) {}

  login(User: UserLogin) {
    this.datePicker.setDefaultDateRange();
    User._ga = this.getGaClientId();
    User.activity_params = this.activityData.collectedActivityData;
    if (this.getCustomCookie('ga_link_id')) {
      User.ga_link_id = toNumber(this.getCustomCookie('ga_link_id'));

      if (this.getCustomCookie('gclid') && typeof this.getCustomCookie('gclid') === 'string') {
        User.gclid = this.getCustomCookie('gclid');
      } else User.gclid = null;
    }

    return this.http.post<Tokens>(`${environment.apiUrl}/auth/login`, User).pipe(
      tap(tokens => {
        this.storeTokens(tokens);
        if (tokens.change_password_required) {
          this.changePasswordRequired = tokens.change_password_required;
        }
      }),
      mapTo({ status: true, error: null, change_password_required: this.changePasswordRequired }),
      catchError(error => {
        return of({ status: false, error });
      })
    );
  }

  loginWithGoogle(token: string): Observable<BaseApiResponse<GoogleVerifyResponse>> {
    const params = { token: token };
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      params
    };
    return this.http.get<BaseApiResponse<GoogleVerifyResponse>>(environment.apiUrl + `/auth/google/verify`, this.httpOptions).pipe(
      tap({
        next: res => {
          if (res.status_code === 200 && res.data) {
            if (res.data.user_access_tokens) {
              this.datePicker.setDefaultDateRange();
              this.storeTokens(res.data.user_access_tokens);
              this.router.navigate(['']);
              this.socketService.setupSocketConnection();
            }
            if (res.data.cache_id && res.data.required_fields) {
              this.registrationWithSocialsData = {
                cache_id: res.data.cache_id,
                required_fields: res.data.required_fields
              };
              this.socialUser = 'google';
              this.router.navigate(['/auth/register']);
            }
          }
        },
        error: (error: HttpErrorResponse) => this.onSocialLoginFail(error)
      })
    );
  }

  loginWithDiscord(token: string): Observable<BaseApiResponse<GoogleVerifyResponse>> {
    const params = { token: token };
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      params
    };
    return this.http.get<BaseApiResponse<GoogleVerifyResponse>>(environment.apiUrl + `/auth/discord/verify`, this.httpOptions).pipe(
      tap({
        next: res => {
          if (res.status_code === 200 && res.data) {
            if (res.data.user_access_tokens) {
              this.datePicker.setDefaultDateRange();
              this.storeTokens(res.data.user_access_tokens);
              this.router.navigate(['']);
              this.socketService.setupSocketConnection();
            }
            if (res.data.cache_id && res.data.required_fields) {
              this.registrationWithSocialsData = {
                cache_id: res.data.cache_id,
                required_fields: res.data.required_fields
              };
              this.socialUser = 'discord';
              localStorage.setItem('discord_auth', 'true');
              this.router.navigate(['/auth/register']);
            }
          }
        },
        error: (error: HttpErrorResponse) => this.onSocialLoginFail(error)
      })
    );
  }

  onSocialLoginFail(error: HttpErrorResponse) {
    console.log(error);
  }

  loginWithCredentials(username: string, password: string, recaptcha: string) {
    // loginWithCredentials(username: string, password: string) {
    const User: UserLogin = {
      grant_type: 'password',
      client_id: environment.client_id,
      client_secret: environment.client_secret,
      username,
      // password
      password,
      recaptcha
    };
    return this.login(User);
  }

  loginWithCredentials2Fa(code: string) {
    const User = {
      grant_type: 'google_2fa',
      client_id: environment.client_id,
      client_secret: environment.client_secret,
      username: this.username,
      password: this.password,
      code
    };
    return this.login(User);
  }

  logout() {
    if (this.socialUser) {
      if (this.registrationWithSocialsData) this.registrationWithSocialsData = undefined;
    }
    this.changePasswordRequired = false;
    let activity_params = this.activityData.collectedActivityData;
    return this.http.put<void>(`${environment.apiUrl}/auth/logout`, { activity_params }).pipe(
      tap(() => {
        this.doLogoutUser();
      }),
      mapTo(true),
      catchError(error => {
        return of(false);
      })
    );
  }

  isLoggedIn() {
    return !!this.getRefreshToken();
  }

  // tokenRefresh() {
  //   const body = {
  //     grant_type: 'refresh_token',
  //     client_id: environment.client_id,
  //     client_secret: environment.client_secret,
  //     refresh_token: this.getRefreshToken()
  //   };
  //   return this.http.post<Tokens>(`${environment.apiUrl}/auth/login`, body).pipe(
  //     tap(tokens => {
  //       this.storeTokens(tokens);
  //     })
  //   );
  // }

  tokenRefresh() {
    // Dodajemy sprawdzenie czy mamy refresh token
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(new Error('No refresh token'));
    }

    const body = {
      grant_type: 'refresh_token',
      client_id: environment.client_id,
      client_secret: environment.client_secret,
      refresh_token: refreshToken
    };

    return this.http.post<Tokens>(`${environment.apiUrl}/auth/login`, body).pipe(
      tap(tokens => {
        if (tokens && tokens.access_token && tokens.refresh_token && tokens.expires_in) {
          this.storeTokens(tokens);
        } else {
          console.error('Invalid token response structure:', tokens);
          throw new Error('Invalid token response');
        }
      }),
      catchError(error => {
        console.error('Token refresh failed:', error);

        // Jeśli błąd to 401 lub 403, automatycznie wyloguj użytkownika
        if (error.status === 401 || error.status === 403) {
          this.removeAuthTokens();
        }

        return throwError(error);
      })
    );
  }

  getAccessToken() {
    return Cookies.get(ACCESS_TOKEN);
  }

  doLogoutUser(): void {
    this.activeModal.close();
    this.removeTokens();
    this.getUserBasicDataService.shareUserDataSubject.next(null);
    this.socketService.disconnect();
    // console.log('doLogoutUser');
  }

  public getRefreshToken() {
    return Cookies.get(REFRESH_TOKEN);
  }

  // for .biz comment domain and secure: false

  private setAccessToken(token: string, expiresIn: number): void {
    const expires = Date.now() + expiresIn * 1000;

    Cookies.set(ACCESS_TOKEN, token, {
      expires: new Date(expires),
      domain: environment.domain,
      secure: true,
      path: '/',
      sameSite: 'lax'
    });
  }

  private setRefreshToken(token: string): void {
    const expires = moment().add(1, 'days').toDate().getTime();
    Cookies.set(REFRESH_TOKEN, token, {
      expires: new Date(expires),
      domain: environment.domain,
      secure: true,
      path: '/',
      sameSite: 'lax'
    });
  }

  private storeTokens({ access_token, refresh_token, expires_in }: Tokens): void {
    // Dodajemy walidację
    if (!access_token || !refresh_token || !expires_in) {
      console.error('Invalid tokens data received:', { access_token: !!access_token, refresh_token: !!refresh_token, expires_in: expires_in });
      throw new Error('Invalid tokens data');
    }

    this.setAccessToken(access_token, expires_in);
    this.setRefreshToken(refresh_token);

    // Ustawiamy timer odświeżania tokenu
    this.setupTokenRefreshTimer(expires_in);
  }

  removeTokens(): void {
    // Usuwamy aktywne timery zanim usuniemy tokeny
    this.clearTokenRefreshTimer();

    const tokensToRemove = ['globalAlertClosed', 'basic_user_data', 'admin_access_token', 'admin_refresh_token', 'campaign_for_widget'];
    tokensToRemove.forEach(token => this.removeSingleToken(token));
    this.removeAuthTokens();
  }

  // for .biz comment domain and secure: false

  removeAuthTokens() {
    this.globalNotifications.hideAll();
    Cookies.remove(ACCESS_TOKEN, {
      domain: environment.domain,
      secure: true,
      path: '/',
      sameSite: 'lax'
    });
    Cookies.remove(REFRESH_TOKEN, {
      domain: environment.domain,
      secure: true,
      path: '/',
      sameSite: 'lax'
    });
  }

  removeSingleToken(token: string): void {
    localStorage.removeItem(token);
  }

  saveUsernamePassword(username: string, password: string): void {
    this.username = username;
    this.password = password;
    this.isLoginInProgress = true;
  }

  checkIsLoginInProgress() {
    return Boolean(this.username && this.password && this.isLoginInProgress);
  }

  /**
   * @TODO update types for this method
   */
  register(userData: Record<string, any>, recaptcha?: string): Observable<any> {
    const params: Record<string, any> = {
      username: userData.username,
      email: userData.email,
      password: userData.password,
      password_confirmation: userData.password_confirmation,
      terms: userData.terms,
      data_processing: userData.data_processing,
      marketing: userData.marketing,
      lang: userData.lang,
      activity_params: this.activityData.collectedActivityData,
      _ga: this.getGaClientId()
    };
    if (recaptcha) {
      params.recaptcha = recaptcha;
    }
    if (this.getCustomCookie('ga_link_id')) {
      params.ga_link_id = toNumber(this.getCustomCookie('ga_link_id'));

      if (this.getCustomCookie('gclid') && typeof this.getCustomCookie('gclid') === 'string') {
        params.gclid = this.getCustomCookie('gclid');
      } else params.gclid = null;
    }
    if (userData.ref !== null) {
      const ref = 'ref';
      params[ref] = userData.ref;
    }
    if (this.getCustomCookie('ref')) {
      params.ref = this.getCustomCookie('ref');
    }
    if (userData.exref.length > 0) {
      const exref = 'exref';
      params[exref] = userData.exref;
    }
    return this.http.post(environment.apiUrl + `/auth/register`, params);
  }

  checkEmail(email: string) {
    const params = {
      email
    };

    return this.http.post<BaseApiResponse<{ message?: string }>>(environment.apiUrl + `/auth/user/check-email`, params);
  }

  checkUsername(username: string) {
    const params = {
      username
    };
    return this.http.post<BaseApiResponse<{ message?: string }>>(environment.apiUrl + `/auth/user/check-username`, params);
  }

  activateUser(token: string) {
    const params = {
      token
    };
    return this.http.post<Tokens>(environment.apiUrl + `/auth/activate`, params);
  }

  resendActivationEmail(data: { email: string; recaptcha: string }) {
    const params = {
      email: data.email,
      recaptcha: data.recaptcha
    };
    return this.http.post<void>(environment.apiUrl + `/auth/user/send-email`, params);
  }

  registerViaSocials(userData: Record<string, any>, cache_id: string) {
    const params: { [p: string]: any } = {
      ...userData,
      cache_id
    };
    if (this.getCustomCookie('ref')) {
      params.ref = this.getCustomCookie('ref');
    }
    return this.http.post<BaseApiResponse<{ message?: string; user_access_tokens: Tokens }>>(environment.apiUrl + `/auth/user/set-username`, params);
  }

  loginAfterRegister(tokens: Tokens) {
    this.storeTokens(tokens);
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.get<BaseApiResponse<IUserBasicInfo>>(environment.apiUrl + `/user/me`, this.httpOptions).pipe(
      tap(user => {
        window.dataLayer.push({
          userId: user?.data?.user?.user_id,
          username: user?.data?.user?.username
        });
      })
    );
  }

  updateUser(data: IUserEditData) {
    const params: Partial<IUserEditData> = {
      phone: data.phone,
      acc_type: data.acc_type,
      name: data.name,
      surname: data.surname,
      country: data.country,
      street: data.street,
      city: data.city,
      post_code: data.post_code,
      currency: data.currency
    };
    if (params.acc_type === 1) {
      const companyName = 'company_name';
      const nip = 'nip';
      const affiliation = 'affiliation';
      params[companyName] = data.company_name;
      params[nip] = data.nip;
      params[affiliation] = data.affiliation;
      if (data.affiliation === 1) {
        const affiliationSource = 'affiliation_source';
        params[affiliationSource] = data.affiliation_source;
      }
    }
    if (data.ref) {
      const ref = 'ref';
      params[ref] = data.ref;
    }
    if ((data.exref || '').length > 0) {
      const exref = 'exref';
      params[exref] = data.exref;
    }
    return this.http.post<void>(environment.apiUrl + `/auth/user/update`, params);
  }

  sendSurvey(data: { source?: number; source_detail?: string; money?: number; social_media?: Record<number, string> }) {
    const params: { source_select?: number; source_detail?: string; money_select?: number; social_media?: Record<number, string> } = {};
    if (data.source) {
      params.source_select = data.source;
      if (data.source === 5 || data.source === 7) {
        params.source_detail = data.source_detail;
      }
    }
    if (data.money) {
      params.money_select = data.money;
    }
    if (data.social_media) {
      params.social_media = data.social_media;
    }

    return this.http.post<void>(environment.apiUrl + `/auth/user/register-completed`, params);
  }

  getUserInfo() {
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.get<BaseApiResponse<UserInfoResponse>>(environment.apiUrl + `/auth/user/info`, this.httpOptions);
  }

  getZipCodeRegex() {
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.get<BaseApiResponse<UserZipCodeRegexResponse>>(environment.apiUrl + `/user/get-zip-code-regex`, this.httpOptions);
  }

  getNipRegex() {
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.get<BaseApiResponse<UserNipRegexResponse>>(environment.apiUrl + `/user/get-nip-regex`, this.httpOptions);
  }

  getSourceInformation() {
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.get<BaseApiResponse<SourceInformationsResponse>>(environment.apiUrl + `/auth/user/source-information`, this.httpOptions);
  }

  sendResetPassEmail(email: string, recaptcha: string) {
    const params = {
      email,
      recaptcha
    };

    return this.http.post<void>(environment.apiUrl + `/auth/password/send-email`, params);
  }

  resetPassword(data: { token: string; email: string; password: string; password_confirmation: string }) {
    return this.http.post<void>(environment.apiUrl + `/auth/password/reset`, { ...data });
  }

  checkIp() {
    return this.http.post<BaseApiResponse<{ status?: boolean }>>(environment.apiUrl + `/auth/ip/check`, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    });
  }

  private setAdminRefreshToken(token: string): void {
    const expires = moment().add(1, 'months').toDate().getTime();

    Cookies.set(ADMIN_REFRESH_TOKEN, token, {
      expires: new Date(expires),
      domain: environment.domain,
      secure: true,
      path: '/',
      sameSite: 'lax'
    });
  }

  private setAdminAccessToken(token: string): void {
    const expires = moment().add(1, 'months').toDate().getTime();
    Cookies.set(ADMIN_ACCESS_TOKEN, token, {
      expires: new Date(expires),
      domain: environment.domain,
      secure: true,
      path: '/',
      sameSite: 'lax'
    });
  }

  private removeAdminTokens(): void {
    Cookies.remove(ADMIN_REFRESH_TOKEN, {
      domain: environment.domain,
      secure: true,
      path: '/',
      sameSite: 'lax'
    });
    Cookies.remove(ADMIN_ACCESS_TOKEN, {
      domain: environment.domain,
      secure: true,
      path: '/',
      sameSite: 'lax'
    });
  }

  private getAdminRefreshToken() {
    return Cookies.get(ADMIN_REFRESH_TOKEN);
  }
  private getAdminAccessToken() {
    return Cookies.get(ADMIN_ACCESS_TOKEN);
  }

  public setAdminTokens(tokens: Tokens): void {
    localStorage.removeItem('basic_user_data');
    localStorage.setItem('remote_login_active', 'active');

    // we're saving refresh token of currently logged admin to let them relogin when dropping remote session

    const currentlyLoggedUserRefreshToken = this.getRefreshToken();
    const currentlyLoggedUserAccessToken = this.getAccessToken();

    if (currentlyLoggedUserRefreshToken && currentlyLoggedUserAccessToken) {
      this.setAdminRefreshToken(currentlyLoggedUserRefreshToken);
      this.setAdminAccessToken(currentlyLoggedUserAccessToken);

      this.removeAuthTokens();
      this.setAccessToken(tokens.access_token, tokens.expires_in);
      this.setRefreshToken(tokens.refresh_token);
    }
  }

  public reloginAdminUser(): void {
    const adminRefreshToken = this.getAdminRefreshToken();
    const adminAccessToken = this.getAdminAccessToken();

    if (adminRefreshToken && adminAccessToken) {
      this.removeTokens();
      this.globalNotifications.hideAll();
      this.removeAdminTokens();
      this.setRefreshToken(adminRefreshToken);
      this.setAccessToken(adminAccessToken, 3600);
    }
    localStorage.removeItem('basic_user_data');
    localStorage.removeItem('remote_login_active');
  }

  private getGaClientId(): string | null {
    const cookie: { [key: string]: string } = {};
    document.cookie.split(';').forEach(function (el) {
      const splitCookie = el.split('=');
      const key = splitCookie[0].trim();
      const value = splitCookie[1];
      cookie[key] = value;
    });
    return cookie['_ga']?.substring(6) ? cookie['_ga'].substring(6) : null; // Adjust the substring based on your actual needs
  }

  public getPublishersNumber() {
    return this.http.get<BaseApiResponse<string>>(environment.apiUrl + `/utils/publishers-count`, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    });
  }

  public decodeHash(hash: string) {
    const params = { hash };
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      params
    };
    return this.http.get<{ redirect?: string; ga_link_id?: number }>(environment.apiUrl + `/ga-links/decode`, this.httpOptions);
  }

  public setCustomCookie(customData: Record<string, string | number>): void {
    const expires = moment().add(1, 'months').toDate().getTime();
    const item = Object.entries(customData)[0];
    Cookies.set(item[0], item[1] as string, {
      expires: new Date(expires),
      domain: environment.domain,
      path: '/',
      secure: true,
      sameSite: 'lax'
    });
  }

  public getCustomCookie(name: string): string | null {
    return Cookies.get(name) || null;
  }

  public removeCustomCookie(name: string) {
    Cookies.remove(name, { domain: environment.domain, path: '/', secure: true, sameSite: 'lax' });
  }

  public setCustomCookieForYear(customData: Record<string, string | number>): void {
    const expires = moment().add(1, 'year').toDate().getTime();
    const item = Object.entries(customData)[0];
    Cookies.set(item[0], item[1] as string, {
      expires: new Date(expires),
      domain: environment.domain,
      path: '/',
      secure: true,
      sameSite: 'lax'
    });
  }

  /**
   * Ustawia timer do proaktywnego odświeżenia tokenu przed jego wygaśnięciem
   * @param expiresIn czas wygaśnięcia w sekundach
   */
  private setupTokenRefreshTimer(expiresIn: number): void {
    // Usuwamy istniejący timer jeśli istnieje
    this.clearTokenRefreshTimer();

    // Obliczamy czas do odświeżenia (5 minut przed wygaśnięciem)
    const timeToRefresh = Math.max((expiresIn - 300) * 1000, 10000); // Minimum 10s, maksymalnie 5min przed wygaśnięciem

    // Dla tokenów z krótszym czasem życia, odświeżamy po połowie czasu życia
    if (expiresIn < 600) {
      // Mniej niż 10 minut
      const halfExpiresIn = Math.floor(expiresIn / 2) * 1000;
      this.tokenRefreshTimer = setTimeout(() => this.refreshTokenSilently(), halfExpiresIn);
      return;
    }

    this.tokenRefreshTimer = setTimeout(() => this.refreshTokenSilently(), timeToRefresh);
  }

  /**
   * Cicha próba odświeżenia tokenu w tle
   */
  private refreshTokenSilently(): void {
    if (!this.getRefreshToken()) {
      console.warn('Silent refresh token failed, no refresh token');
      return;
    }

    this.tokenRefresh().subscribe({
      error: error => {
        console.error('Proactive token refresh failed:', error);
      }
    });
  }

  /**
   * Czyści timer odświeżania tokenu
   */
  private clearTokenRefreshTimer(): void {
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  /**
   * Sprawdza czy access token jest jeszcze ważny
   * @returns true jeśli token jest ważny, false jeśli wygasł lub nie istnieje
   */
  public isTokenValid(): boolean {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return false;
    }

    try {
      // Access token to JWT - sprawdzamy czy wygasł
      const tokenParts = accessToken.split('.');
      if (tokenParts.length !== 3) {
        return false; // Nieprawidłowy format JWT
      }

      const payload = JSON.parse(atob(tokenParts[1]));
      const expiryTime = payload.exp * 1000; // exp jest w sekundach, konwertujemy na milisekundy

      // Sprawdzamy czy token wygasł
      return Date.now() < expiryTime;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }

  public recaptchaTest(recaptcha: string) {
    return this.http.post<BaseApiResponse>(`https://api.mylead.global/api/recaptcha-test`, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      params: {
        recaptcha
      }
    });
  }

  public getPasswordChangeFormFields() {
    return this.http.get<BaseApiResponse<{ fields: string[] }>>(environment.apiUrl + `/user/required-change-password/fields`, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    });
  }

  public changePassword(data: { token?: string; old_password: string; password: string; password_confirmation: string }) {
    return this.http.post<void>(environment.apiUrl + `/user/required-change-password/update`, { ...data });
  }
}
